import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MatLegacyTable as MatTable } from '@angular/material/legacy-table';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { AccountService } from 'app/core';
import { FieldDef, User } from 'app/core/models';
import { SelectableFieldDef } from 'app/shared/components/column-selector/column-selector.types';
import { ReadableFieldPipe } from 'app/shared/pipes/readable-field.pipe';
import { LegacyTasksTabService } from '../../services/tasks.service';
import { Task, TaskStatus, TaskType } from '../../tasks.types';
import { Column, ColumnConfig, FieldCustom } from '../list.types';
import get from 'lodash-es/get';
import lodashGroupBy from 'lodash-es/groupBy';
import moment from 'moment';
import { Project } from 'app/modules/projects/projects.types';
import { ConfirmDialogComponent } from 'app/shared/components/widgets/dialog/confirm/confirm.component';

@Component({
  selector: 'div[app-task-table]',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LegacyTaskTableComponent extends OnDestroyMixin implements AfterViewInit, AfterViewChecked {
  private _selectedTask: Task;
  private _tasks: Task[] = [];
  private wasTasksUndefined = true;
  private lastTaskListId: number;
  private _isAggregatedTask: boolean;

  private columnsConfig: ColumnConfig = {
    prefix: ['drag_control', 'expand_control'],
    dynamic: [],
    suffix: ['details_control'],
  };

  public columns: Column[] = [];
  public grouppedTasks = [];
  private _groupBy: string;

  @Input() set groupBy(value: string) {
    this._groupBy = value;
    this.groupTasks(value);
  }

  @Input() data: any;
  @Input() project: Project;
  @Input() users: User[];
  @Input() taskStatuses: TaskStatus[];
  @Input() taskFields: FieldDef[];
  @Input() condensedStatusView = true;
  @Input() canEdit = true;
  maxWidth = 0;

  @Input() set isAggregatedTask(value) {
    this._isAggregatedTask = value;
    if (value) {
      this.columnsConfig.prefix = this.columnsConfig.prefix.filter(
        (column) => column !== 'drag_control' && column !== 'expand_control',
      );
    }
  }

  @Input() set selectedTask(value: Task) {
    if (value) {
      this._selectedTask = value;
      const parentId = this._selectedTask.parent_id;
      const parent = this.tasks.find(({ id }) => id === parentId);
      let index = -1;
      if ((index = parent?.children?.findIndex(({ id }) => id === -1)) > -1) {
        parent.children[index].id = this._selectedTask.id;
        this.renderChildTableRows(parentId);
      }
    }
  }

  @Input() set selectedFields(value: SelectableFieldDef[]) {
    if (value) {
      this.columnsConfig.dynamic = value;
      this.columns = [...this.columnsConfig.prefix, ...this.columnsConfig.dynamic, ...this.columnsConfig.suffix].filter(
        (column) => !!column,
      );
    }
  }

  @Input() set tasks(value: Task[]) {
    if (value && !this.isAggregatedTask) {
      const taskListId = value[0]?.task_list_id;
      this._tasks = [...value];

      if (this.wasTasksUndefined || this.lastTaskListId !== taskListId) {
        this.wasTasksUndefined = false;
      }
      this.lastTaskListId = taskListId;
      this.closingSections();
    } else if (value) {
      this._tasks = [{ children: [...value] }];
    }

    if (this._groupBy) {
      this.groupTasks(this._groupBy);
    }
    this.handleTableRenderForSelectedTask();
  }

  get isAggregatedTask(): boolean {
    return this._isAggregatedTask;
  }

  @Output() orderChanged = new EventEmitter<Task[]>();
  @Output() updateTask = new EventEmitter<Task>();
  @Output() createTask = new EventEmitter<{ taskType: TaskType; parentId?: number; rank?: number }>();
  @Output() navigateToTask = new EventEmitter<Task>();
  @Output() navigateToTaskComment = new EventEmitter<Task>();
  @Output() refreshTasks = new EventEmitter<void>();

  @ViewChildren('childTable') matTables: QueryList<MatTable<any>>;

  get dynamicColumns(): Column[] {
    return this.columnsConfig.dynamic;
  }

  getColumnsHeaders(isChild = false): string[] {
    if (isChild) {
      return this.columns
        .map((column) => (typeof column === 'string' ? column : column.name))
        .filter((column) => column !== 'drag_control' && column !== 'expand_control');
    }
    return this.columns.map((column) => (typeof column === 'string' ? column : column.name));
  }

  get tasks(): Task[] {
    return this._groupBy ? this.grouppedTasks : this._tasks;
  }

  get selectedTask(): Task {
    return this._selectedTask;
  }

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    private readonly accountService: AccountService,
    private readonly taskService: LegacyTasksTabService,
    private readonly matDialog: MatDialog,
  ) {
    super();
  }

  ngAfterViewInit(): void {
    this.closingSections();
  }

  ngAfterViewChecked(): void {
    this.setTaskContainerMaxWidth();
  }

  stageNotReached(task: Task) {
    if (this.project && task.task_type === 'section' && task.project_stage) {
      return this.project.project_stage?.rank < task.project_stage?.rank;
    }
    return false;
  }

  groupTasks(groupBy: string): void {
    this.grouppedTasks = [];
    if (groupBy) {
      const groupByReplaceMap = {
        project_id: 'project.title',
        property_id: 'property.title',
        project_stage_id: 'project_stage',
        project_phase_id: 'project_phase',
        assigned_task_role_id: 'assigned_task_role_name',
      };
      groupBy = this.taskFields.find(({ name }) => name === groupBy)?.meta?.sorting ?? groupBy;
      groupBy = groupByReplaceMap[groupBy] || groupBy;
      this._tasks.forEach((task) => {
        const grouppedTasks = lodashGroupBy(task.children, (b) => get(b, groupBy, 'Other'));
        if (grouppedTasks['null']) {
          grouppedTasks['Other'] = [...grouppedTasks['null']];
          delete grouppedTasks['null'];
        }
        if (grouppedTasks['']) {
          grouppedTasks['Other'] = [...grouppedTasks['null']];
          delete grouppedTasks[''];
        }

        grouppedTasks['Other'];

        let groups = Object.keys(grouppedTasks).map((key) => ({
          isGroup: true,
          totalCount: grouppedTasks[key].length,
          key,
          label: this.formatGroupLabel(key, groupBy),
        }));

        //groups = sortBy(groups, ['key']);

        const result = groups.reduce((acc, group) => {
          const tasks = grouppedTasks[group.key];
          return [...acc, group, ...tasks];
        }, []);

        this.grouppedTasks.push({ ...task, children: result });
      });
    }
  }

  private formatGroupLabel(value, key): string {
    switch (key) {
      case 'workflow_state':
        return this.taskStatuses.find(({ key }) => key === value)?.label;
      case 'due_date':
      case 'start_date':
      case 'created_at':
      case 'updated_at':
      case 'completed_at':
        return value === 'Other' ? 'Other' : moment(value).format('MMM DD, y');
      default:
        return value;
    }
  }

  getDynamicColumnValue(row: Task, column: FieldCustom | FieldDef): any {
    const customFieldValue = row.custom_field_values.find(({ name }) => name === column.name);
    const customFieldDef = this.data.taskFieldDefs.find((x) => x.name === column.name);

    if (customFieldDef?.type === 'checkbox') {
      let values = customFieldValue?.val;
      return new ReadableFieldPipe(this.locale, this.accountService).transform(
        values,
        { [column.name]: customFieldValue?.val },
        { ...customFieldDef, ...customFieldValue },
        this.data.options,
      );
    }

    if (customFieldValue && customFieldDef) {
      let values = customFieldValue.val;

      if (customFieldDef.meta.multi_select) {
        values = row.custom_field_values.filter((field) => field.name === column.name).map(({ val }) => val);
      }
      return new ReadableFieldPipe(this.locale, this.accountService).transform(
        values,
        { [column.name]: customFieldValue.val },
        { ...customFieldDef, ...customFieldValue },
        this.data.options,
      );
    }

    return row[column.name] || (row.task_type === 'task' && column.name === 'title' ? 'Task Title' : '');
  }

  sectionClick(task: Task): void {
    task.expanded = !task.expanded;
    this.taskService
      .updateTask(task.task_list_id, null, task, false, false)
      .pipe(untilComponentDestroyed(this))
      .subscribe();
  }

  isSubtask(task: Task): boolean {
    return this.taskService.isSubtask(task, this.tasks);
  }

  listDropped(event: CdkDragDrop<Task[]>, taskId?: number): void {
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);

      this.renderChildTableRows(taskId);

      event.container.data.forEach((value: Task, index: number) => (value.rank = index));

      this.orderChanged.emit(event.container.data);
    }
  }

  whenIsGroupItem(index: number, data: Task): boolean {
    return data.isGroup;
  }

  whenIsKeyDate(index: number, data: Task): boolean {
    return data.task_type === 'key_date';
  }

  whenIsTask(index: number, data: Task): boolean {
    return data.task_type === 'task';
  }

  relayCreateTask(model: { taskType: TaskType; parentId: number }): void {
    const { taskType, parentId } = model;
    const placeholderTask: Partial<Task> = {
      id: -1,
      rank: this.getNextRank(model.parentId),
      task_type: taskType,
      title: '',
      workflow_state: 'not_started',
      children: [],
      custom_field_values: [],
    };

    this.tasks.find(({ id }) => id === parentId)?.children?.push(placeholderTask);

    this.createTask.emit({ ...model, rank: placeholderTask.rank });
  }

  relayUpdateTask(task: Task): void {
    this.updateTask.emit(task);
  }

  relayUpdateTaskStartDate(task: Task): void {
    if (task.predecessors && task.predecessors.length > 0) {
      this.matDialog
        .open(ConfirmDialogComponent, {
          autoFocus: false,
          data: {
            title: 'Confirmation',
            message: `Manually changing the 'Start Date' will result in the removal of all predecessor relationships. Do you wish to proceed?`,
          },
        })
        .afterClosed()
        .pipe(untilComponentDestroyed(this))
        .subscribe((result) => {
          if (result) {
            task.predecessors = [];
            this.updateTask.emit(task);
          } else {
            this.refreshTasks.emit();
          }
        });
    } else {
      this.updateTask.emit(task);
    }
  }

  relayNavigateToTask(row: Task, column: Partial<FieldCustom> | Partial<FieldDef>): void {
    if (
      !['start_date', 'due_date', 'assigned_user_id', 'project_id', 'property_id', 'task_list_id'].includes(column.name)
    ) {
      this.navigateToTask.emit(row);
    }
  }

  private closingSections() {
    this._tasks = this._tasks.map((task) => {
      if (this.stageNotReached(task)) task.expanded = false;
      return task;
    });
  }

  private getNextRank(parentId: number): number {
    const ranks = [0, ...this.tasks.find(({ id }) => id === parentId)?.children?.map(({ rank }) => rank)];
    return Math.max(...ranks) + 1 || 0;
  }

  private handleTableRenderForSelectedTask(): void {
    if (this.selectedTask?.parent_id) {
      this.renderChildTableRows(this.selectedTask?.parent_id);
    }
  }

  private renderChildTableRows(parentTaskId: number): void {
    const matTableClass = `child-table-${parentTaskId}`;
    this.matTables
      .find(({ _elementRef: { nativeElement } }: any) => nativeElement.classList.contains(matTableClass))
      ?.renderRows();
  }

  private setTaskContainerMaxWidth(): void {
    const tables = document.querySelectorAll('table');
    tables.forEach((table) => {
      if (table.offsetWidth >= this.maxWidth) {
        this.maxWidth = table.offsetWidth;
      }
    });

    const sectionContainers = document.querySelectorAll('.section-container');
    sectionContainers.forEach((sectionContainer) => {
      if (sectionContainer && this.maxWidth > 0) {
        sectionContainer.setAttribute('style', `width: ${this.maxWidth}px`);
      }
    });
  }

  navigateToComment(event: MouseEvent, row: Task) {
    event.stopPropagation();
    this.navigateToTaskComment.emit(row);
  }
}
